/**
 * Field-level diff of two JSON payloads that the ledger recorded as one change.
 *
 * The resource-audit ledger diffs a table ROW, and `raw_data`, `variants`,
 * `options`, `images` and `raw_line_items` are TEXT columns holding a
 * serialized payload. So a one-key edit inside a Shopify product arrives as a
 * single `{field: 'raw_data', old: '<4KB>', new: '<4KB>'}` entry, and the
 * change history's answer to "what changed" was the whole blob.
 *
 * The result is a TREE, because the view that renders it is a hierarchical
 * table. Handing back dotted paths and letting the view split them again would
 * be a second, weaker parser: a Shopify metafield key containing a `.` makes
 * that split silently wrong, and nothing fails. The walk already knows the
 * structure — it says so.
 *
 * Deliberately NOT a dependency: this is ~120 lines and the repo's dependency
 * blacklist (project CLAUDE.md) requires an ADR before a new library.
 */
import { parseIfString } from '@resource-kit/ui/react';

export type DiffNodeKind = 'leaf' | 'object' | 'array';

/**
 * One row of the comparison — a leaf value, or a container holding more rows.
 *
 * Both raw sides are carried on every node, container rows included, so the
 * view never has to infer WHY a container is marked changed. `undefined` means
 * the key is absent on that side; `null` means it is present and cleared.
 * Collapsing those two would erase the difference between "this key was
 * removed" and "this column was emptied", which the panel renders differently.
 */
export interface DiffNode {
  /** Display label: an object key, or `[3]` for an array element. */
  key: string;
  /** Dotted path from the payload root, for copy and for flat summaries. */
  path: string;
  kind: DiffNodeKind;
  before: unknown;
  after: unknown;
  /** Empty for a leaf. */
  children: readonly DiffNode[];
  /** This row itself differs: a leaf whose value moved, or a container that gained or lost a side. */
  selfChanged: boolean;
  /**
   * How much of this subtree (this row included) counts as changed.
   *
   * Leaves are the unit. A container that lost a side does NOT add itself on
   * top of its leaves — `{metafields: [{a, b}]} → {}` is two changed fields,
   * not four — because once the loss is visible leaf by leaf, the leaves ARE
   * the count. A container only speaks for itself when it has no leaves to
   * speak for it: `{metafields: []} → {}` is one change, and without that it
   * would be zero and vanish from a view filtering on this number.
   */
  changedCount: number;
  /** Rows in this subtree, this row included. */
  totalCount: number;
}

export interface JsonDiffTree {
  /** The payload root. Never rendered as a row itself. */
  root: DiffNode;
  /** Changed rows beneath the root. Zero means the two payloads are equivalent. */
  changedCount: number;
  /** Rows beneath the root. */
  totalCount: number;
}

/** Mirrors `JsonKeyValueTable`'s ceiling: circular refs and pathological depth. */
const MAX_DEPTH = 64;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStructure = (value: unknown): boolean => Array.isArray(value) || isPlainObject(value);

const isNullish = (value: unknown): boolean => value === undefined || value === null;

const joinPath = (parent: string, key: string): string => (parent ? `${parent}.${key}` : key);

/** The union of both sides' keys, in before-order then after-only. */
function unionKeys(before: unknown, after: unknown): string[] {
  const keys = isPlainObject(before) ? Object.keys(before) : [];
  const seen = new Set(keys);
  if (isPlainObject(after)) {
    for (const key of Object.keys(after)) {
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }
  return keys;
}

/**
 * One node and everything under it.
 *
 * Descends when both sides are the same kind of structure, and ALSO when one
 * side is a structure and the other is nullish. That second case is the whole
 * point: a key whose array disappeared, or a column cleared to `null`, used to
 * fall through to "not both structures" and become one leaf whose value was
 * the entire stringified payload.
 *
 * A scalar replaced by a structure still does NOT descend — both sides carry a
 * value there, and a scalar is not a shape the object can be aligned against.
 * That is why the condition reads "the other side is nullish" and not "the
 * other side is not a structure".
 */
function buildNode(
  before: unknown,
  after: unknown,
  key: string,
  path: string,
  depth: number,
): DiffNode {
  const bothObjects = isPlainObject(before) && isPlainObject(after);
  const bothArrays = Array.isArray(before) && Array.isArray(after);
  const oneSideNullish =
    (isNullish(before) && isStructure(after)) || (isNullish(after) && isStructure(before));

  if (depth >= MAX_DEPTH || (!bothObjects && !bothArrays && !oneSideNullish)) {
    const changed = !Object.is(before, after);
    return {
      key,
      path,
      kind: 'leaf',
      before,
      after,
      children: [],
      selfChanged: changed,
      changedCount: changed ? 1 : 0,
      totalCount: 1,
    };
  }

  const kind: DiffNodeKind = Array.isArray(before) || Array.isArray(after) ? 'array' : 'object';
  const children: DiffNode[] = [];

  if (kind === 'array') {
    const beforeLength = Array.isArray(before) ? before.length : 0;
    const afterLength = Array.isArray(after) ? after.length : 0;
    for (let index = 0; index < Math.max(beforeLength, afterLength); index += 1) {
      // A missing side contributes `undefined`, never the parent's `null`: the
      // container is what was cleared, and a leaf claiming `→ null` would be
      // inventing a value the payload never held.
      children.push(
        buildNode(
          Array.isArray(before) ? before[index] : undefined,
          Array.isArray(after) ? after[index] : undefined,
          `[${index}]`,
          `${path}[${index}]`,
          depth + 1,
        ),
      );
    }
  } else {
    for (const childKey of unionKeys(before, after)) {
      children.push(
        buildNode(
          isPlainObject(before) ? before[childKey] : undefined,
          isPlainObject(after) ? after[childKey] : undefined,
          childKey,
          joinPath(path, childKey),
          depth + 1,
        ),
      );
    }
  }

  const selfChanged = oneSideNullish;
  const childChanged = children.reduce((sum, child) => sum + child.changedCount, 0);

  return {
    key,
    path,
    kind,
    before,
    after,
    children,
    selfChanged,
    changedCount: childChanged > 0 ? childChanged : selfChanged ? 1 : 0,
    totalCount: 1 + children.reduce((sum, child) => sum + child.totalCount, 0),
  };
}

/**
 * The comparison tree for two JSON payloads, or `null` when this is not a
 * JSON-payload change at all.
 *
 * Structure-shaped values only, on BOTH sides. Deciding by SHAPE rather than by
 * a column allowlist is the point: `raw_data` is declared by no frontend schema
 * (products and customers dropped their raw_data-derived fields in #1405), so a
 * name list could not reach it, while shape reaches `variants`, `options` and
 * `images` for free. A quoted scalar (`"123"`, `"true"`) parses but has no
 * fields, and must keep its ordinary inline rendering.
 *
 * A key-order-only difference — real, since the backend compares the serialized
 * column — comes back as a tree with `changedCount: 0` rather than a separate
 * `kind`. One number the callers already read, one fewer branch to keep in sync.
 */
export function structuralJsonDiff(before: unknown, after: unknown): JsonDiffTree | null {
  const parsedBefore = parseIfString(before);
  const parsedAfter = parseIfString(after);
  if (!isStructure(parsedBefore) || !isStructure(parsedAfter)) return null;

  const root = buildNode(parsedBefore, parsedAfter, '', '', 0);
  return {
    root,
    changedCount: root.changedCount,
    // The root is never a rendered row, so its own tally is not part of the total.
    totalCount: root.totalCount - 1,
  };
}

/**
 * The payload behind a value the ledger recorded, or `null` when the value is
 * not one. Used for the ONE-sided case — a column that was empty and became a
 * payload, and the create/delete snapshots — where there is nothing to diff
 * against but a key/value table still beats a 4KB line.
 */
export function asJsonStructure(value: unknown): unknown | null {
  const parsed = parseIfString(value);
  return isStructure(parsed) ? parsed : null;
}
